import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FileSpreadsheet } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { isManager } from "@/lib/access";
import { downloadFile } from "@/components/InvoiceRegister";
import { F } from "@/components/vault/common";

const REPORTS = [["overdue", "Overdue files", "Files not returned by their expected date"], ["inventory", "Location-wise inventory", "Every file grouped by premises, unit, shelf and section"],
  ["issues", "Issue & return history", "All outward and inward movements"], ["movements", "Location movements", "Every transfer between locations"],
  ["files", "File master", "All files A–Z with status, holder and document count"], ["audit", "Audit trail", "Every action, timestamped (managers)"]];

const Tile = ({ label, value, testid }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid={testid}><div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div><div className="text-3xl mono font-semibold mt-1">{value ?? "—"}</div></div>
);

export default function VaultReports() {
  const { user } = useAuth();
  const mgr = isManager(user);
  const [s, setS] = useState(null);
  const [set, setSet] = useState(null);
  useEffect(() => {
    api.get("/vault/summary").then((r) => setS(r.data)).catch(() => {});
    api.get("/vault/settings").then((r) => setSet({ ...r.data, file_types: r.data.file_types.join("\n"), doc_types: r.data.doc_types.join("\n") })).catch(() => {});
  }, []);
  const save = async () => {
    try {
      await api.put("/vault/settings", { prefix: set.prefix, file_types: set.file_types.split("\n"), doc_types: set.doc_types.split("\n") });
      toast.success("File Vault settings saved");
    } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div data-testid="vault-reports-page" className="space-y-6">
      <div>
        <div className="text-[11px] uppercase tracking-wider text-slate-500">File Vault</div>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Reports & settings</h1>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Tile label="Files" value={s?.total} testid="vault-sum-total" /><Tile label="In storage" value={s?.in_storage} testid="vault-sum-in" />
        <Tile label="Issued out" value={s?.issued} testid="vault-sum-issued" /><Tile label="Overdue" value={s?.overdue} testid="vault-sum-overdue" />
        <Tile label="Documents indexed" value={s?.documents} testid="vault-sum-docs" /><Tile label="Locations" value={s?.locations} testid="vault-sum-locs" />
      </div>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
        {REPORTS.filter(([k]) => k !== "audit" || mgr).map(([k, l, d]) => (
          <div key={k} className="bg-white border border-slate-200 rounded-lg p-4 flex items-center justify-between gap-3">
            <div><div className="text-sm font-semibold">{l}</div><div className="text-xs text-slate-500">{d}</div></div>
            <Button variant="outline" data-testid={`vault-export-${k}`} onClick={() => downloadFile(`/vault/reports/${k}.csv`, {}, `file-vault-${k}.csv`)} className="gap-2 bg-white shrink-0"><FileSpreadsheet className="w-4 h-4 text-emerald-700" />CSV</Button>
          </div>
        ))}
      </div>
      {mgr && set && (
        <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4" data-testid="vault-settings">
          <div className="text-sm font-semibold">Settings</div>
          <F label="File number prefix (numbers look like PREFIX/YEAR/0001)"><Input data-testid="vs-prefix" value={set.prefix} onChange={(e) => setSet({ ...set, prefix: e.target.value })} className="max-w-[160px] mono" /></F>
          <div className="grid md:grid-cols-2 gap-4">
            <F label="File types (one per line)"><Textarea data-testid="vs-file-types" rows={8} value={set.file_types} onChange={(e) => setSet({ ...set, file_types: e.target.value })} /></F>
            <F label="Document types (one per line)"><Textarea data-testid="vs-doc-types" rows={8} value={set.doc_types} onChange={(e) => setSet({ ...set, doc_types: e.target.value })} /></F>
          </div>
          <Button data-testid="vs-save" onClick={save} className="bg-blue-700 hover:bg-blue-800 text-white">Save settings</Button>
        </div>
      )}
    </div>
  );
}
